/**
 * Baked figure frames: the frames people in the game use most, drawn ahead of time by
 * `npm run bake` (scripts/assets/bake.mjs, tools/export/bake.ts) into one atlas per look. A frame found
 * here is cut from its atlas instead of being drawn; everything else is still drawn as before (see
 * `fetch` in sets.ts).
 *
 * There are two sets (`BAKE_SETS`), one per pixel density (density.ts), and a page loads only its
 * own (`bakeSet`): `public/bake/2x/`, the figure-grid frames as the workers draw them (option C, the
 * tiers ปานกลาง to สูงมาก), and `public/bake/1x/`, every one of those frames brought down ÷2 at bake
 * time (`toArtRes`, the tiers ต่ำมาก and ต่ำ), so the rig is not drawn twice.
 *
 * Files are fetched through the cache in this browser (`fetchAsset`, cache/assets.ts), kept by
 * content hash, so a second visit reads them from IndexedDB.
 *
 * Each set carries its own stamp: the hash of the art code it was drawn with (`virtual:art-hash`,
 * see vite.config.ts) and the set's name (`setHash`). Once any drawing code changes they no longer
 * match, and figures are drawn live until they are baked again, so a bake never shows stale art,
 * nor the other set's.
 */
import ART_HASH from 'virtual:art-hash';
import { fetchAsset } from '../../cache/assets';
import { bakeSet } from '../density';
import { figureOutlineTag } from '../figureOutlineFlag';
import { truePxFigures } from '../truepx/flags';
import { atRes, canvas, ctx2d } from '../pixel';
import type { Pose, View8 as View } from '../sprites';
import type { BladeLine } from './weapons';

/** Where a baked frame lies in its atlas, and where its top-left sits in the full frame (`artX`). */
export type BakedRect = [x: number, y: number, w: number, h: number, artX: number, artY: number];

/** One look's atlas description, next to its PNG. */
export interface BakeAtlas {
  hash: string;
  /** Canvas pixels per art pixel of its frames (1 when left out, as older bakes were). */
  res?: number;
  frames: Record<string, BakedRect>;
  blades?: Record<string, BladeLine[]>;
}

/**
 * A look's frames come in two atlases: `core` (standing, blinking, walking, running, the stance,
 * single poses, putting the weapon away and drawing it), loaded with the look, and `motion` (the
 * fighting moves, for the looks monsters wear), loaded when first needed.
 */
export const BAKE_GROUPS = ['core', 'motion'] as const;
export type BakeGroup = (typeof BAKE_GROUPS)[number];
export const groupOf = (pose: Pose): BakeGroup => (pose === 'motion' ? 'motion' : 'core');

/** Which looks are baked (by `lookId`, with their groups and names), for which art and set. */
export interface BakeManifest {
  hash: string;
  set?: BakeSet;
  looks: Record<string, { name: string; groups: BakeGroup[] }>;
}

export const BAKE_DIR = 'bake/';
/** The bake sets, and how many canvas pixels per art pixel their frames hold. */
export const BAKE_SETS = { '2x': 2, '1x': 1 } as const;
export type BakeSet = keyof typeof BAKE_SETS;
/** A set's stamp: the art code's hash and the set's name. */
export const setHash = (set: BakeSet, art: string = ART_HASH): string => `${art}-${set}`;

/** Names a frame of a look. */
export const frameKey = (view: View, pose: Pose, ph: number, lag: number, blink: boolean): string =>
  `${view}|${pose}|${ph}|${lag}|${blink ? 1 : 0}`;

/** A short file name for a look's key (`Appearance.key`): two 32-bit FNV-1a hashes. */
export function lookId(key: string): string {
  let a = 2166136261;
  let b = 2166136261 ^ 0x5bd1e995;
  for (let i = 0; i < key.length; i++) {
    const ch = key.charCodeAt(i);
    a = Math.imul(a ^ ch, 16777619);
    b = Math.imul(b ^ ch, 16777619) ^ (b >>> 13);
  }
  return (a >>> 0).toString(16).padStart(8, '0') + (b >>> 0).toString(16).padStart(8, '0');
}

/** The art code's hash, as the running client was built from it. */
export const artHash = (): string => ART_HASH;

/**
 * Frames of baked looks found baked, and asked for but drawn instead, by pose (for checking a
 * bake is in use and covers what is asked for).
 */
export const bakeStats = { hits: 0, misses: 0, missed: {} as Record<string, number> };
function miss(pose: Pose, why: string): undefined {
  bakeStats.misses++;
  const k = `${why}:${pose}`;
  bakeStats.missed[k] = (bakeStats.missed[k] ?? 0) + 1;
  return undefined;
}

type Loaded = { img: ImageBitmap; atlas: BakeAtlas };
let manifest: Promise<BakeManifest | null> | null = null;
let known: BakeManifest | null = null;
const atlases = new Map<string, Loaded | 'loading' | 'none'>();

const url = (file: string) => `${import.meta.env.BASE_URL}${BAKE_DIR}${bakeSet()}/${file}`;
/** This page's set's stamp. */
const stamp = () => setHash(bakeSet());

/** The manifest, if there is one baked for this art. */
function loadManifest(): Promise<BakeManifest | null> {
  manifest ??= (async () => {
    try {
      const res = await fetchAsset(url('manifest.json'));
      if (!res.ok) return null;
      const m = (await res.json()) as BakeManifest;
      return (known = m.hash === stamp() ? m : null);
    } catch {
      return null;
    }
  })();
  return manifest;
}

/** An atlas's file name (without extension). */
export const atlasName = (id: string, group: BakeGroup): string => `${id}-${group}`;

/** Loads one atlas (`atlasName`). */
async function loadAtlas(name: string): Promise<void> {
  atlases.set(name, 'loading');
  try {
    const [res, png] = await Promise.all([fetchAsset(url(`${name}.json`)), fetchAsset(url(`${name}.png`))]);
    if (!res.ok || !png.ok) throw new Error('missing');
    const atlas = (await res.json()) as BakeAtlas;
    if (atlas.hash !== stamp()) throw new Error('stale');
    atlases.set(name, { atlas, img: await createImageBitmap(await png.blob()) });
  } catch {
    atlases.set(name, 'none');
  }
}

/**
 * Loads the bakes for these looks (`Appearance.key`s), resolving once they are ready (or known
 * not to be baked): figures seen at the start then show baked frames from their first.
 */
export async function loadBakes(keys: readonly string[]): Promise<void> {
  if (typeof document === 'undefined') return;
  const m = await loadManifest();
  if (!m) return;
  const ids = [...new Set(keys.map(lookId))];
  // The fighting moves follow without holding up the start: a fight rarely starts at once.
  for (const id of ids)
    if (m.looks[id]?.groups.includes('motion') && !atlases.has(atlasName(id, 'motion')))
      void loadAtlas(atlasName(id, 'motion'));
  const core = ids.filter((id) => m.looks[id]?.groups.includes('core'));
  await Promise.all(
    core
      .map((id) => atlasName(id, 'core'))
      .filter((name) => !atlases.has(name))
      .map(loadAtlas),
  );
}

/**
 * A frame of a look cut from its bake, if it was baked for this art and the atlas is loaded. An
 * atlas not loaded yet starts loading, and the frame is drawn as usual meanwhile.
 */
export function bakedFrame(
  key: string,
  view: View,
  pose: Pose,
  ph: number,
  lag: number,
  blink: boolean,
): { c: HTMLCanvasElement; blades: BladeLine[] } | undefined {
  // Lane C0-TRUEPX-GAME: the bake holds frames without the true-pixel clean-up; such a page draws its own.
  if (truePxFigures() || figureOutlineTag()) return undefined;
  if (!known) {
    if (!manifest && typeof document !== 'undefined') void loadManifest();
    return undefined;
  }
  const id = lookId(key);
  const group = groupOf(pose);
  const name = atlasName(id, group);
  const got = atlases.get(name);
  if (!known.looks[id]?.groups.includes(group)) return undefined;
  if (!got) {
    void loadAtlas(name);
    return miss(pose, 'loading');
  }
  if (got === 'loading' || got === 'none') return miss(pose, got);
  const k = frameKey(view, pose, ph, lag, blink);
  const r = got.atlas.frames[k];
  if (!r) return miss(pose, lag ? 'cloth' : 'unbaked');
  bakeStats.hits++;
  const [x, y, w, h, ax, ay] = r;
  const c = canvas(w, h);
  ctx2d(c).drawImage(got.img, x, y, w, h, 0, 0, w, h);
  return { c: atRes(c, got.atlas.res ?? 1, ax, ay), blades: got.atlas.blades?.[k] ?? [] };
}
